import {Trans} from '@lingui/macro';
import React from 'react';

import SalesforceLogoTiny from '../../../../../../../../../src/assets/images/integrations/salesforce16.svg';
import salesforceLogoSm from '../../../../../../../../../src/assets/images/integrations/salesforce20.svg';
import salesforceLogo from '../../../../../../../../../src/assets/images/integrations/salesforce24.svg';
import {CrmToFrontCustomFieldMapping} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {
  CrmSyncDefaultFieldMappingIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {CrmSyncOptionsEnum, SelectedOption} from '../crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageConfig} from './crmSyncsPageConfig';
import {
  computeFrontAccountDefaultFieldIcon,
  computeFrontCardDefaultFieldIcon,
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncCardDefaultFieldIdsEnum,
  renderAccountDefaultFieldName,
  renderFrontCardDefaultFieldName,
} from './crmSyncsPageConfigHelpers';

/*
 * Constants.
 */

enum SalesforceDefaultAccountFieldsEnum {
  NAME = 'Name',
  DOMAINS = 'Website',
  DESCRIPTION = 'Description',
}

const salesforceRequiredAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: SalesforceDefaultAccountFieldsEnum.NAME,
    customFieldId: CrmSyncDefaultFieldMappingIdsEnum.NAME,
  },
];

const salesforceOptionalAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: SalesforceDefaultAccountFieldsEnum.DOMAINS,
    customFieldId: CrmSyncDefaultFieldMappingIdsEnum.DOMAINS,
  },
  {
    crmFieldId: SalesforceDefaultAccountFieldsEnum.DESCRIPTION,
    customFieldId: CrmSyncDefaultFieldMappingIdsEnum.DESCRIPTION,
  },
];

enum SalesforceDefaultCardFieldsEnum {
  NAME = 'Name',
  EMAIL = 'Email',
  PHONE = 'Phone',
  ACCOUNT_ID = 'AccountId',
}

const salesforceRequiredCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: SalesforceDefaultCardFieldsEnum.NAME,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.NAME,
  },
];

const salesforceOptionalCardMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: SalesforceDefaultCardFieldsEnum.EMAIL,
    customFieldId: CrmSyncCardDefaultFieldIdsEnum.EMAIL,
  },
];

enum SalesforceFieldTypesEnum {
  ADDRESS = 'address',
  BOOLEAN = 'boolean',
  COMBOBOX = 'combobox',
  CURRENCY = 'currency',
  DATE = 'date',
  DATETIME = 'datetime',
  DOUBLE = 'double',
  EMAIL = 'email',
  ID = 'id',
  INT = 'int',
  LOCATION = 'location',
  MULTI_PICKLIST = 'multipicklist',
  PERCENT = 'percent',
  PHONE = 'phone',
  PICKLIST = 'picklist',
  REFERENCE_USER = 'referenceuser',
  REFERENCE_ACCOUNT = 'referenceaccount',
  STRING = 'string',
  TEXTAREA = 'textarea',
  TIME = 'time',
  URL = 'url',
}

const maxAccountsToSync = 100000;
const maxCardsToSync = 10000000;

/*
 * Config.
 */

export const salesforceConfig: CrmSyncsPageConfig = {
  logo: salesforceLogo,
  logoSm: salesforceLogoSm,
  logoTiny: SalesforceLogoTiny,
  helpCenterLink: 'https://help.front.com/t/83hbpb1',
  displayName: 'Salesforce',
  [CrmSyncIntegrationResourceTypesEnum.ACCOUNT]: {
    maxNumberToSync: maxAccountsToSync,
    buildExternalUrl,
    syncOptionsTitle: (
      <Trans id="crm-syncs-page-salesforce-accounts-options-title">Choose which accounts to sync</Trans>
    ),
    syncOptions: {
      [CrmSyncOptionsEnum.ALL]: {
        optionText: (
          <Trans id="crm-syncs-page-salesforce-accounts-sync-all-accounts-option">All accounts</Trans>
        ),
        maxCountError: (
          <Trans id="crm-syncs-page-salesforce-all-accounts-max-count-error">
            Account limit (100k) exceeded. Please select a Salesforce list view.
          </Trans>
        ),
      },
      [CrmSyncOptionsEnum.LISTS]: {
        optionText: (
          <Trans id="crm-syncs-page-salesforce-accounts-sync-lists-option">
            From a Salesforce account list view
          </Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-salesforce-lists-option-tooltip">
            You can create and customize list views in Salesforce based on specific filters.
          </Trans>
        ),
        maxCountError: (
          <Trans id="crm-syncs-page-salesforce-lists-option-max-count-error">
            Lists with more than {maxAccountsToSync.toLocaleString()} accounts cannot be synced.
          </Trans>
        ),

        dropdownPlaceholder: (
          <Trans id="crm-syncs-page-salesforce-lists-option-dropdown-placeholder">Select list view</Trans>
        ),
        dropdownTitle: (
          <Trans id="crm-syncs-page-salesforce-lists-option-dropdown-title">Salesforce list views</Trans>
        ),
        learnMoreLink:
          'https://trailhead.salesforce.com/en/content/learn/modules/lex_customization/lex_customization_list',
      },
    },
    syncMapping: {
      description: (
        <Trans id="crm-syncs-page-salesforce-accounts-mapping-description">
          Select which Salesforce fields to sync to Front and map them to a Front account custom field.
        </Trans>
      ),
      defaultCrmFieldType: SalesforceFieldTypesEnum.STRING, // if we don't have a crm field type, use string
      computeRequiredMapping: () => salesforceRequiredAccountMapping,
      computeOptionalMapping: () => salesforceOptionalAccountMapping,
      computeCrmDefaultFieldIcon: (crmField?: CrmFieldModel) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmField?.id),
      computeFrontDefaultFieldIcon: computeFrontAccountDefaultFieldIcon,
      computeFrontDefaultFieldName: renderAccountDefaultFieldName,
      computeCrmDefaultFieldPlaceholderName: computeCrmAccountDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: (crmFieldId: string) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId, 'mentionSmall'),
      renderSpecialFieldMessage: (frontFieldId?: number) => {
        if (frontFieldId === CrmSyncAccountDefaultFieldIdsEnum.DOMAINS) {
          return (
            <Trans id="crm-syncs-page-accounts-mapping-domain-message">
              Contacts with matching email domains will be auto-associated with their account
            </Trans>
          );
        }
        return undefined;
      },
      filterCrmFields: (crmFields) => crmFields,
    },
  },
  [CrmSyncIntegrationResourceTypesEnum.CARD]: {
    maxNumberToSync: maxCardsToSync,
    buildExternalUrl,
    syncOptionsTitle: (
      <Trans id="crm-syncs-page-salesforce-cards-options-title">Choose which cards to sync</Trans>
    ),
    syncOptions: {
      [CrmSyncOptionsEnum.ACCOUNTS]: {
        optionText: (
          <Trans id="crm-syncs-page-salesforce-cards-sync-from-accounts-option">
            From accounts chosen above
          </Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-salesforce-cards-from-accounts-option-tooltip">
            Accounts sync must be enabled
          </Trans>
        ),
      },
      [CrmSyncOptionsEnum.LISTS]: {
        optionText: (
          <Trans id="crm-syncs-page-salesforce-cards-sync-lists-option">
            From a Salesforce contact list view
          </Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-salesforce-cards-lists-option-tooltip">
            You can create and customize list views in Salesforce based on specific filters.
          </Trans>
        ),
        maxCountError: (
          <Trans id="crm-syncs-page-salesforce-cards-lists-option-max-count-error">
            Lists with more than {maxCardsToSync.toLocaleString()} cards cannot be synced.
          </Trans>
        ),

        dropdownPlaceholder: (
          <Trans id="crm-syncs-page-salesforce-cards-lists-option-dropdown-placeholder">
            Select list view
          </Trans>
        ),
        dropdownTitle: (
          <Trans id="crm-syncs-page-salesforce-cards-lists-option-dropdown-title">
            Salesforce list views
          </Trans>
        ),
        learnMoreLink:
          'https://trailhead.salesforce.com/en/content/learn/modules/lex_customization/lex_customization_list',
      },
    },
    syncMapping: {
      description: (
        <Trans id="crm-syncs-page-salesforce-cards-mapping-description">
          Select the Salesforce fields you would like to sync to Front, and map them to a Front contact custom
          field. Contacts will be matched via handles.
        </Trans>
      ),
      defaultCrmFieldType: SalesforceFieldTypesEnum.STRING, // if we don't have a crm field type, use string
      computeRequiredMapping: computeRequiredCardMapping,
      computeOptionalMapping: () => salesforceOptionalCardMapping,
      computeCrmDefaultFieldIcon: computeCrmCardFieldIcon,
      computeFrontDefaultFieldIcon: computeFrontCardDefaultFieldIcon,
      computeFrontDefaultFieldName: renderFrontCardDefaultFieldName,
      computeCrmDefaultFieldPlaceholderName: computeCrmCardDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: computeCrmCardDefaultFieldPlaceholderIcon,
      renderSpecialFieldMessage: () => undefined,
      filterCrmFields: (crmFields) =>
        crmFields?.filter((crmField) => crmField.id !== SalesforceDefaultCardFieldsEnum.ACCOUNT_ID),
    },
  },
};

/*
 * Helpers.
 */

function buildExternalUrl(baseUrl: string, id: string) {
  return `${baseUrl}/${id}`;
}

function computeCrmAccountDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case SalesforceDefaultAccountFieldsEnum.NAME:
      return 'Account Name';
    case SalesforceDefaultAccountFieldsEnum.DOMAINS:
      return 'Website';
    case SalesforceDefaultAccountFieldsEnum.DESCRIPTION:
      return 'Description';
    default:
      return undefined;
  }
}

function computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId?: string, defaultIconName?: IconName) {
  switch (crmFieldId) {
    case SalesforceDefaultAccountFieldsEnum.DOMAINS:
      return 'mentionSmall';
    case SalesforceDefaultAccountFieldsEnum.NAME:
    case SalesforceDefaultAccountFieldsEnum.DESCRIPTION:
      return 'contentSmall';
    default:
      return defaultIconName;
  }
}

function computeCrmCardDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case SalesforceDefaultCardFieldsEnum.NAME:
      return 'Name';
    case SalesforceDefaultCardFieldsEnum.ACCOUNT_ID:
      return 'accountId';
    default:
      return undefined;
  }
}

function computeCrmCardFieldIcon(crmField?: CrmFieldModel) {
  switch (crmField?.type) {
    case SalesforceFieldTypesEnum.EMAIL:
      return 'mentionSmall';
    case SalesforceFieldTypesEnum.PHONE:
      return 'phoneSmall';
    case SalesforceFieldTypesEnum.REFERENCE_ACCOUNT:
      return 'contentSmall';
    default:
      return undefined;
  }
}

function computeCrmCardDefaultFieldPlaceholderIcon(crmFieldId?: string) {
  switch (crmFieldId) {
    case SalesforceDefaultCardFieldsEnum.EMAIL:
      return 'mentionSmall';
    case SalesforceDefaultCardFieldsEnum.PHONE:
      return 'phoneSmall';
    case SalesforceDefaultCardFieldsEnum.ACCOUNT_ID:
    case SalesforceDefaultCardFieldsEnum.NAME:
    default:
      return 'contentSmall';
  }
}

function computeRequiredCardMapping(selectedOption?: SelectedOption) {
  if (selectedOption?.type === CrmSyncOptionsEnum.LISTS) {
    return salesforceRequiredCardMapping;
  }
  return [
    ...salesforceRequiredCardMapping,
    {
      crmFieldId: SalesforceDefaultCardFieldsEnum.ACCOUNT_ID,
      customFieldId: CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID,
    },
  ];
}
